import { useCallback, useMemo } from 'react';
import type { ChangeEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Checkbox } from '@cbhq/cds-web/controls/Checkbox';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Text } from '@cbhq/cds-web/typography/Text';

import type { MemberRole, MemberRoleMap } from '../../utils/constants';

import type { ProductIconName } from './ProductIcon';
import { ProductIcon } from './ProductIcon';
import { ProductRolePopover } from './ProductRolePopover';

const messages = defineMessages({
  multipleRolesSummary: {
    id: 'Team.inviteTray.multipleRolesSummary',
    defaultMessage: '{count} roles',
    description: 'Summary label shown on a product row when multiple roles are selected',
  },
  noAccessSummary: {
    id: 'Team.inviteTray.noAccessSummary',
    defaultMessage: 'No access',
    description: 'Summary label shown on a product row when no roles are selected',
  },
});

export type ProductAccessRowConfig = {
  productKey: string;
  iconName: ProductIconName;
  iconBackground: string;
  title: string;
  description: string;
  onCheckedChange?: (checked: boolean) => void;
  showCheckbox?: boolean;
  roleOptions: MemberRoleMap[];
  selectedRoleValues: MemberRole[];
  onRoleToggle: (value: MemberRole, isSelected: boolean) => void;
  isEditable?: boolean;
};

export function ProductAccessRow({
  productKey,
  iconName,
  iconBackground,
  title,
  description,
  onCheckedChange,
  showCheckbox = true,
  roleOptions,
  selectedRoleValues,
  onRoleToggle,
  isEditable = true,
}: ProductAccessRowConfig) {
  const { formatMessage } = useIntl();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onCheckedChange?.(event.target.checked);
    },
    [onCheckedChange],
  );

  // Each selected role already encodes its product and legal-entity scope in the role id.
  const checked = selectedRoleValues.length > 0;

  const roleLabel = useMemo(() => {
    if (selectedRoleValues.length > 1) {
      return formatMessage(messages.multipleRolesSummary, { count: selectedRoleValues.length });
    }
    if (selectedRoleValues.length === 0) {
      return formatMessage(messages.noAccessSummary);
    }
    return (
      roleOptions.find((option) => option.value === selectedRoleValues[0])?.name ??
      roleOptions[roleOptions.length - 1]?.name ??
      ''
    );
  }, [formatMessage, roleOptions, selectedRoleValues]);

  return (
    <HStack width="100%" alignItems="center" gap={2}>
      <HStack flexGrow={1} alignItems="center" gap={2}>
        {showCheckbox ? (
          <Checkbox
            value={productKey}
            accessibilityLabel={title}
            checked={checked}
            onChange={handleChange}
            disabled={!isEditable}
            controlSize={22}
            borderRadius={0}
            background={checked ? 'fg' : undefined}
            borderColor={checked ? 'fg' : undefined}
          />
        ) : null}
        <HStack flexGrow={1} alignItems="center" gap={1.5}>
          <ProductIcon name={iconName} background={iconBackground} />
          <VStack gap={0} alignItems="flex-start" flexGrow={1}>
            <Text as="span" font="label1" color="fg" noWrap>
              {title}
            </Text>
            <Text as="span" font="label2" color="fgMuted" noWrap>
              {description}
            </Text>
          </VStack>
        </HStack>
      </HStack>
      <ProductRolePopover
        roleOptions={roleOptions}
        selectedRoleValues={selectedRoleValues}
        onRoleToggle={onRoleToggle}
        roleLabel={roleLabel}
        productLabel={title}
        disabled={roleOptions.length === 0 || !isEditable}
      />
    </HStack>
  );
}
